import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, DeniedState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime, formatDate, isoDate } from "@/lib/dates";
import { ShieldCheck, Search, Filter, X, Info, Download, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 20;

const ENTITY_TYPES = ["", "risk", "control", "finding", "acceptance", "user", "tenant", "framework", "framework_mapping", "appetite", "report", "mitigation"];

export default function Audit() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [detail, setDetail] = useState(null);

  const [q, setQ] = useState("");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const activeFilterCount = useMemo(
    () => [actor, action, entityType, dateFrom, dateTo].filter(Boolean).length,
    [actor, action, entityType, dateFrom, dateTo]
  );

  const buildParams = (targetPage) => {
    const params = new URLSearchParams();
    params.set("page", String(targetPage));
    params.set("page_size", String(PAGE_SIZE));
    if (q) params.set("q", q);
    if (actor) params.set("actor", actor);
    if (action) params.set("action", action);
    if (entityType) params.set("entity_type", entityType);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return params;
  };

  const load = async (targetPage = 1, includeChain = true) => {
    setLoading(true);
    try {
      const calls = [api.get(`/audit?${buildParams(targetPage).toString()}`)];
      if (includeChain) calls.push(api.get("/audit/verify"));
      const results = await Promise.all(calls);
      const resp = results[0].data;
      if (Array.isArray(resp)) {
        // legacy flat response — fall back to client-side paging
        setItems(resp);
        setTotal(resp.length);
        setTotalPages(Math.max(1, Math.ceil(resp.length / PAGE_SIZE)));
      } else {
        setItems(resp.items || []);
        setTotal(resp.total || 0);
        setTotalPages(resp.pages || 1);
      }
      setPage(targetPage);
      if (includeChain) setChain(results[1].data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(1, true); /* eslint-disable-next-line */ }, []);

  const reset = () => {
    setQ(""); setActor(""); setAction(""); setEntityType(""); setDateFrom(""); setDateTo("");
    // Applying reset re-fetches page 1 in the next tick (state batches)
    setTimeout(() => load(1, false), 0);
  };

  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = items;
  const goPage = (p) => load(Math.max(1, Math.min(totalPages, p)), false);

  const exportCsv = async () => {
    // Fetch the whole filtered slice for export (up to server cap of 2000).
    try {
      const params = new URLSearchParams();
      params.set("limit", "2000");
      if (q) params.set("q", q);
      if (actor) params.set("actor", actor);
      if (action) params.set("action", action);
      if (entityType) params.set("entity_type", entityType);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/audit?${params.toString()}`);
      const rowsRaw = Array.isArray(data) ? data : (data.items || []);
      if (!rowsRaw.length) { toast.error("No rows to export."); return; }
      const header = ["seq", "created_at", "actor_email", "action", "entity_type", "entity_id", "hash", "prev_hash", "payload"];
      const escape = (v) => {
        if (v == null) return "";
        const s = typeof v === "string" ? v : JSON.stringify(v);
        return `"${s.replace(/"/g, '""')}"`;
      };
      const rows = rowsRaw.map((e) => header.map((h) => escape(e[h])).join(","));
      const csv = [header.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_${isoDate()}_${rowsRaw.length}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rowsRaw.length} event${rowsRaw.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!can("audit.read")) return <div className="max-w-7xl mx-auto px-6 py-10"><DeniedState requiredRoles={["Admin", "RiskManager", "Approver"]} /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="audit-page">
      <PageHeader
        kicker="Immutable · hash-chained · 30-day retention"
        title="Audit Trail"
        description="Append-only log, SHA-256 chained. Records auto-purge after 30 days. Search, filter, and drill into every action."
        actions={
          <div className="flex items-center gap-2">
            {chain && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border ${chain.valid ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-rose-50 text-rose-800 border-rose-300"}`} data-testid="chain-status">
                <ShieldCheck className="h-3.5 w-3.5" /> Chain {chain.valid ? "valid" : "broken"} · {chain.count} events
              </span>
            )}
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!total} data-testid="audit-export-csv">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => load(1, true)} data-testid="audit-refresh">Refresh</Button>
          </div>
        }
      />

      {/* Search + filter bar */}
      <Card className="p-4 mb-4" data-testid="audit-search-card">
        <form onSubmit={(e) => { e.preventDefault(); load(1, false); }} className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[280px]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search actions, entities, actors, IDs…"
                className="pl-8"
                data-testid="audit-search-input"
              />
            </div>
          </div>
          <Button type="submit" className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="audit-search-submit">Search</Button>
          <Button type="button" variant="outline" onClick={() => setShowFilters((v) => !v)} data-testid="audit-toggle-filters">
            <Filter className="h-3.5 w-3.5 mr-1.5" />
            Filters {activeFilterCount > 0 && <span className="ml-1.5 rounded-full bg-cyan-700 text-white text-[10px] px-1.5 py-0.5 font-mono">{activeFilterCount}</span>}
          </Button>
          {(q || activeFilterCount > 0) && (
            <Button type="button" variant="ghost" onClick={reset} data-testid="audit-reset">
              <X className="h-3.5 w-3.5 mr-1.5" /> Clear
            </Button>
          )}
        </form>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4 pt-4 border-t border-slate-100" data-testid="audit-filter-panel">
            <div>
              <Label>Actor email</Label>
              <Input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="alice@" data-testid="filter-actor" />
            </div>
            <div>
              <Label>Action</Label>
              <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="risk. / auth. / report." data-testid="filter-action" />
            </div>
            <div>
              <Label>Entity type</Label>
              <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="filter-entity">
                {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t || "Any"}</option>)}
              </select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" />
            </div>
            <div className="lg:col-span-5 flex justify-end">
              <Button type="button" size="sm" onClick={() => load(1, false)} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="filter-apply">Apply filters</Button>
            </div>
          </div>
        )}
      </Card>

      {loading ? <LoadingSkeleton /> : (
        <Card className="overflow-hidden" data-testid="audit-table-card">
          <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-slate-500">
            <span data-testid="audit-count">{total} event{total === 1 ? "" : "s"} · Page {currentPage} of {totalPages}</span>
            <span className="text-slate-400">Click a row for details</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white border-b"><tr className="text-left">
                {["#", "Action", "Entity", "Actor", "When", "Hash"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {pageItems.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setDetail(e)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`audit-${e.seq}`}
                  >
                    <td className="px-3 py-2 font-mono tabular-nums text-slate-500">#{e.seq}</td>
                    <td className="px-3 py-2"><span className="font-mono text-[11px] font-semibold text-cyan-800 bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5">{e.action}</span></td>
                    <td className="px-3 py-2 text-slate-700 text-xs">
                      <div>{e.entity_type}</div>
                      <div className="text-[10px] font-mono text-slate-400">{(e.entity_id || "").slice(0, 8)}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 text-xs font-mono">{e.actor_email}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{formatDateTime(e.created_at)}</td>
                    <td className="px-3 py-2 text-[10px] font-mono text-slate-400" title={e.hash}>{e.hash.slice(0, 10)}…</td>
                  </tr>
                ))}
                {!pageItems.length && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">No audit events match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3 text-xs" data-testid="audit-pagination">
              <div className="font-mono text-slate-500 tabular-nums">
                Showing <span className="font-semibold text-slate-800">{pageStart + 1}</span>–<span className="font-semibold text-slate-800">{Math.min(pageStart + PAGE_SIZE, total)}</span> of <span className="font-semibold text-slate-800">{total}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => goPage(1)} disabled={currentPage === 1} data-testid="audit-page-first">« First</Button>
                <Button variant="outline" size="sm" onClick={() => goPage(currentPage - 1)} disabled={currentPage === 1} data-testid="audit-page-prev">
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </Button>
                <span className="px-2.5 font-mono font-semibold text-slate-700 tabular-nums" data-testid="audit-page-indicator">
                  {currentPage} / {totalPages}
                </span>
                <Button variant="outline" size="sm" onClick={() => goPage(currentPage + 1)} disabled={currentPage === totalPages} data-testid="audit-page-next">
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => goPage(totalPages)} disabled={currentPage === totalPages} data-testid="audit-page-last">Last »</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Drill-down */}
      {detail && (
        <Dialog open={true} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-2xl" data-testid="audit-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <Info className="h-4 w-4 text-cyan-700" /> Audit event #{detail.seq}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Action" value={detail.action} mono />
                <Field label="Entity" value={`${detail.entity_type} · ${(detail.entity_id || "").slice(0, 8)}`} mono />
                <Field label="Actor" value={detail.actor_email} mono />
                <Field label="Timestamp" value={formatDateTime(detail.created_at)} mono />
                <Field label="Hash" value={detail.hash} mono small />
                <Field label="Prev hash" value={detail.prev_hash} mono small />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-1.5">Payload / changes</div>
                <pre className="bg-slate-900 text-slate-100 rounded-md p-4 text-[11.5px] font-mono overflow-auto max-h-96" data-testid="audit-detail-payload">
{JSON.stringify(detail.payload || {}, null, 2)}
                </pre>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Field({ label, value, mono, small }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-0.5">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${small ? "text-[11px]" : "text-sm"} text-slate-800 break-all`}>{value}</div>
    </div>
  );
}
